import { css } from "@emotion/react";
import { defaultCurrency } from "@zapier/currency";
import { ButtonProps, Text } from "@zapier/design-system";
import { usePricingPageSize } from "@/hooks";
import { usePricingPageContext } from "@/pages/pricing";

import { FeatureListing, PlanTypeEnum, Size } from "@/types";
import { PlanCard } from "./PlanCard/PlanCard";

import { useRendererContext } from "@zapier/builder.io-kit";

import { PlanPrice } from "./PlanCard/PlanPrice";
import { PlanUnavailableDescription } from "./PlanCard/PlanUnavailableDescription";
import {
  getEdgePlansByTask,
  getHasPlanInRange,
  getTooltipPosition,
} from "./utils";

type PlanCardCollectionV2Props = {
  stateKey: string;
  plans?: {
    type: string;
    description: string;
    primaryCTA?: { text?: string; color?: ButtonProps["color"] };
    hidePrice?: boolean;
    secondaryCTA?: { text?: string; href?: string };
    features?: FeatureListing[];
    tag?: string;
    pricingText?: string;
    footerLink?: { text: string; href: string };
  }[];
};

const Styles = {
  root: css({
    maxWidth: "1200px",
    padding: "0 20px",
    margin: "auto",
  }),

  cards: css({
    maxWidth: "1200px",
    display: "grid",
    gridTemplateColumns: "1fr",
    position: "relative",
    gap: "25px",

    "@media (min-width: 715px)": {
      gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    },

    "@media (min-width: 896px)": {
      gridTemplateColumns: "repeat(auto-fit, minmax(256px, 1fr))",
    },

    "@media (min-width: 1024px)": {
      gridTemplateColumns: "repeat(auto-fit, minmax(288px, 1fr))",
      gap: "30px",
    },

    "@media (min-width: 1235px)": {
      gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    },
  }),
  pricingText: css({
    width: "75%",
  }),
};

export default function PlanCardCollectionV2({
  plans = [],
  stateKey,
}: PlanCardCollectionV2Props) {
  const { useStateByKey } = useRendererContext();

  const [{ isAnnual, selectedCurrency, selectedTasks }] = useStateByKey(
    stateKey,
    {
      isAnnual: true,
      selectedCurrency: defaultCurrency,
      selectedTasks: 0,
    }
  );

  const { planStacks } = usePricingPageContext();

  const size: Size = usePricingPageSize();
  const hasPlanInRange = getHasPlanInRange(planStacks, selectedTasks);

  return (
    <div css={Styles.root}>
      <div css={Styles.cards}>
        {plans.map((plan, index) => {
          const planStack = planStacks.find(
            (planStack) => planStack.planType === plan.type
          );

          if (!planStack) {
            return null;
          }
          const interval = isAnnual ? "year" : "month";

          const { minPlan, maxPlan, isTaskTooHigh, hasTaskTier } =
            getEdgePlansByTask(
              planStack.plans.filter((plan) => plan.interval === interval),
              selectedTasks,
              planStack.planType === PlanTypeEnum.Free
            );

          const selectedPlan =
            planStack.plans.find(
              (plan) =>
                plan.interval === interval && plan.tasks === selectedTasks
            ) || minPlan;

          return (
            <div key={plan.type}>
              <PlanCard
                renderPriceDetails={() => {
                  if (plan.pricingText) {
                    return (
                      <div css={Styles.pricingText}>
                        <Text color="neutral800" type="subHeader1Bold">
                          {plan.pricingText}
                        </Text>
                      </div>
                    );
                  } else if (plan.hidePrice) {
                    return <div></div>;
                  } else if (hasTaskTier) {
                    return (
                      <PlanPrice
                        currency={selectedCurrency}
                        isAnnual={isAnnual}
                        showPriceTitle={
                          planStack.planType !== PlanTypeEnum.Free &&
                          selectedTasks === 0
                        }
                      >
                        {selectedPlan.amount}
                      </PlanPrice>
                    );
                  }
                  return (
                    <PlanUnavailableDescription
                      isTaskTooHigh={isTaskTooHigh}
                      minPlan={minPlan}
                      maxPlan={maxPlan}
                      currency={selectedCurrency}
                      isCustomLimit={!hasPlanInRange}
                      isAnnual={isAnnual}
                    />
                  );
                }}
                currency={selectedCurrency}
                selectedPlan={selectedPlan ?? minPlan}
                description={plan.description}
                features={plan.features}
                tag={plan.tag}
                footerLink={plan.footerLink}
                primaryCTA={plan.primaryCTA}
                secondaryCTA={plan.secondaryCTA}
                previousPlanType={index > 0 ? plans[index - 1].type : null}
                tooltipPosition={getTooltipPosition(
                  index,
                  size,
                  planStacks.length
                )}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
